import { Cart, CartLineNode, CartAttribute } from '@/lib/shopify';
import { getCartLineItemProperty } from '@/utils/shopify';

export function shouldUsePlayGeniusCheckout(cart: Cart | null): boolean {
  if (!cart?.lines?.edges?.length) {
    return false;
  }

  for (const edge of cart.lines.edges) {
    const node = edge.node;

    if (shouldLineItemUsePlayGeniusCheckout(node)) {
      return true;
    }
  }

  return false;
}

const PRODUCT_HANDLES = [
  'subscription-gifting',
  'reading-skill-set',
  'the-bath-set',
];

const PROPERTIES_MUST_BE_TRUE = ['is_playgenius_subscription'];

const PROPERTIES_ANY_VALUE = [
  'is_book_bundle',
  'price_source',
  'subscription_plan_settings',
];

const SOURCES = ['sub_shop'];

const COURSE_PROPERTIES = [
  'is_course_pack',
  'is_skill_set',
  '_is_course_pack',
  '_is_skill_set',
];

function getCartProperty(
  name: string,
  attributes: any,
): string | null | undefined {
  return (
    getCartLineItemProperty(name, attributes) ??
    getCartLineItemProperty(`_${name}`, attributes)
  );
}

function shouldLineItemUsePlayGeniusCheckout(node: CartLineNode): boolean {
  const productHandle = node.merchandise?.product?.handle;

  if (productHandle && PRODUCT_HANDLES.includes(productHandle)) {
    return true;
  }

  for (const property of PROPERTIES_MUST_BE_TRUE) {
    if (getCartProperty(property, node.attributes) === 'true') {
      return true;
    }
  }

  for (const property of PROPERTIES_ANY_VALUE) {
    if (getCartProperty(property, node.attributes)) {
      return true;
    }
  }

  const source = getCartProperty('source', node.attributes);
  if (source && SOURCES.includes(source)) {
    return true;
  }

  for (const property of COURSE_PROPERTIES) {
    if (getCartProperty(property, node.attributes) === 'true') {
      return true;
    }
  }

  return false;
}

/**
 * Utilities for converting cart properties for Shopify checkout
 */

export function isPropertyHidden(property: string): boolean {
  const firstCharacter = property?.[0];
  return firstCharacter === '_';
}

export function convertPublicPropertiesToPrivateProperties(
  attributes: CartAttribute[],
): CartAttribute[] {
  return attributes.map((attribute) => {
    if (isPropertyHidden(attribute.key)) {
      // Already hidden, return as is
      return attribute;
    } else {
      // Convert to hidden by adding underscore prefix
      return {
        key: '_' + attribute.key,
        value: attribute.value,
      };
    }
  });
}

export function shouldConvertCartItemsToHidden(cart: Cart | null): boolean {
  if (!cart?.lines?.edges?.length) return false;

  // Check if all items have been converted to hidden properties
  const allItemsHaveBeenConverted = cart.lines.edges.every((edge) =>
    edge.node.attributes.every((attr) => isPropertyHidden(attr.key)),
  );

  return !allItemsHaveBeenConverted;
}
